import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { allPackageTypes } from '../../../../fulfillment-planner/package/dropdown/package-group-resolver';
import { emptyObject, emptyArray } from 'ui-components/default-props/empty';
import Global from '../../../../store/global-selectors';
import { getRateResponseValidationErrorMeta } from './rate-response-validation-errors';
import { PackageService } from '../../../../services/package-service';
import { convertToWeightUnitEnum } from '../../../../services/unit-services/weights/weight-unit-enum-converter';
import {
  convertToDimensionUnitEnum,
  convertFromDimensionUnitEnum,
} from '../../../../services/unit-services/dimensions/dimension-unit-enum-converter';
import { useReadFromScale } from '../../../../hooks/use-read-from-scale';
import { useGetSelectedLabelProviderIds } from '../../../../wallet/modals/wallet-registration/hooks/use-get-selected-label-providerIds';
import { RateBrowserConfigurationForm } from './index';
import { calculateRateBrowserWeight } from '../../../../services/unit-services/weights/weight-convert-service';
import { useCountryConfig } from '../../../../hooks/use-country-config';
import { CountryConfigNames } from '../../../../contexts/country-config-context/country-config-enums';

const onSubmit = (onBrowseRates) => {
  return ({
    shipFromId,
    shipFromPostalCode,
    shipToPostalCode,
    shipToCity,
    shipToCountryCode,
    isResidential,
    weight,
    packageId,
    dimensions,
    confirmationType,
    serviceClassificationId,
    pickupId,
    driverTip,
  }) => {
    onBrowseRates({
      shipFromId,
      shipFromPostalCode,
      shipToPostalCode,
      shipToCity,
      shipToCountryCode,
      isResidential,
      weight: {
        weightUnit: convertToWeightUnitEnum(weight.unit),
        weight: weight.value,
      },
      packageId,
      packageTypeId: PackageService.parseInformationFromCompositePackageId(packageId).packageTypeId,
      dimensions: {
        ...dimensions,
        unit: convertToDimensionUnitEnum(dimensions.unit),
      },
      confirmationType,
      serviceClassificationId: serviceClassificationId === 'show-all' ? undefined : serviceClassificationId,
      pickupId,
      driverTip,
    });
  };
};

export const RateBrowserConfigurationFormContainer = ({
  isFromCSWRateBrowserClick = false,
  onBrowseRates,
  rateViewerKey,
  onScaleRead = null,
}) => {
  const configureRatesModel = useSelector(Global.selectRateBrowsergetConfigureRatesModel) || emptyObject;
  const [, rateBrowserDefaultsConfig] = useCountryConfig(CountryConfigNames.RateBrowserDefaults);

  const {
    postalCode: defaultPostalCode = '',
    cityOrSuburb: defaultCityOrSuburb = '',
    length: defaultLength = 0,
    width: defaultWidth = 0,
    height: defaultHeight = 0,
  } = rateBrowserDefaultsConfig || {};

  const defaultShipFrom = useSelector(Global.selectSellerDefaultShipFrom);
  const shipFromId = configureRatesModel.shipFromId || defaultShipFrom?.shipFromId;

  const shipFromPostalCode = configureRatesModel.shipFromPostalCode || defaultShipFrom?.postalCode || '';
  const sellerHomeCountry = useSelector(Global.selectSellerHomeCountry);
  const shipToPostalCode = configureRatesModel.shipToPostalCode || defaultPostalCode;
  const shipToCityOrSuburb = configureRatesModel.shipToCityOrSuburb || defaultCityOrSuburb;
  const shipToCountryCode = configureRatesModel.shipToCountryCode || sellerHomeCountry;

  const isShipToResidentialAddress = !!configureRatesModel.isResidential;
  const packageTypeId = configureRatesModel?.selectedPackage?.packageTypeId || allPackageTypes.id;
  const sellerPackages = [allPackageTypes, ...useSelector(Global.selectSellerPackages)];
  const rateViewerSelectorProps = { rateViewerKey };
  const sellerProviderRates = useSelector((state) => Global.selectRateViewerRates(state, rateViewerSelectorProps));
  const selectedSellerProviderId = useSelector((state) => Global.selectRateViewerSellerProviderId(state, rateViewerSelectorProps));
  const labelProviderIds = useGetSelectedLabelProviderIds(selectedSellerProviderId ? [selectedSellerProviderId] : []);
  const selectedLabelProviderId = labelProviderIds ? labelProviderIds[0] : '';
  const providerRates = sellerProviderRates.find((rates) => rates.sellerLabelProviderId === selectedSellerProviderId);
  const { hasDimensionsError } = getRateResponseValidationErrorMeta(providerRates);
  const selectedPackage = sellerPackages.find((pkg) => !!pkg && !pkg.isHidden && pkg.packageTypeId === packageTypeId) || emptyObject;
  const { packageId } = selectedPackage;
  const sellerCurrencyCode = useSelector(Global.selectSellerDefaultCurrency);
  const packages = configureRatesModel?.packages || emptyArray;
  const packageIds = packages?.map((p) => p.packageId);

  const confirmationOptionId =
    !configureRatesModel.confirmationType || configureRatesModel.confirmationType === 'None' ? '0' : configureRatesModel.confirmationType;
  const serviceClassificationId = configureRatesModel.serviceClassificationId || 'show-all';

  const sellerDefaultDimensionUnit = useSelector(Global.selectSellerDefaultDimensionUnit);
  const defaultWeightUnit = useSelector(Global.selectSellerDefaultWeightUnit);

  const configureRatesModelDimensions = configureRatesModel.dimensions || emptyObject;
  const packageDimensions = {
    unit: convertFromDimensionUnitEnum(
      configureRatesModel.dimensions?.unit === 'Unknown' ? sellerDefaultDimensionUnit || 'Unknown' : configureRatesModel.dimensions?.unit,
    ),
    length: configureRatesModelDimensions.length || defaultLength,
    width: configureRatesModelDimensions.width || defaultWidth,
    height: configureRatesModelDimensions.height || defaultHeight,
  };

  const baseConfigureRatesModelWeight = configureRatesModel.weight || emptyObject;
  const configureRatesModelWeight = baseConfigureRatesModelWeight;

  const weightScaleInformation = {
    value: configureRatesModelWeight.weight,
    requestId: configureRatesModelWeight.requestId,
  };

  const combinedPackage = PackageService.combineMultiPackages(packages);

  const weight = calculateRateBrowserWeight(configureRatesModelWeight, combinedPackage, defaultWeightUnit);
  const [weightAndDims, setWeightAndDims] = useState({
    weight,
    packageDimensions,
    shouldUseScale: false,
  });

  const onScaleReadInternal = useCallback(
    (newValue) => {
      if (onScaleRead) {
        onScaleRead(newValue);
      }
      if (newValue.dims) {
        setWeightAndDims({
          weight: newValue.weight,
          packageDimensions: newValue.dims,
          shouldUseScale: true,
        });
      } else {
        setWeightAndDims((s) => ({
          ...s,
          weight: newValue.weight,
          shouldUseScale: true,
        }));
      }
    },
    [setWeightAndDims, onScaleRead],
  );

  const onReadFromScale = useReadFromScale(onScaleReadInternal);
  const setShouldUseScale = useCallback((value) => setWeightAndDims({ ...weightAndDims, shouldUseScale: value }), [weightAndDims]);

  const props = {
    shipFromId,
    shipFromPostalCode,
    shipToPostalCode,
    shipToCityOrSuburb,
    shipToCountryCode,
    isShipToResidentialAddress,
    packageId,
    sellerCurrencyCode,
    sellerDefaultDimensionUnit,
    confirmationOptionId,
    serviceClassificationId,
    onSubmit: onSubmit(onBrowseRates),
    hasDimensionsError,
    isFromCSWRateBrowserClick,
    weightScaleInformation,
    onReadFromScale,
    setShouldUseScale,
    shouldUseScale: weightAndDims?.shouldUseScale,
    weight: weightAndDims?.weight ?? weight,
    packageDimensions: weightAndDims?.packageDimensions ? weightAndDims.packageDimensions : packageDimensions,
    selectedLabelProviderId,
    isMultiPackage: packages.length > 1,
    packageIds,
  };

  return <RateBrowserConfigurationForm {...props} />;
};

RateBrowserConfigurationFormContainer.propTypes = {
  isFromCSWRateBrowserClick: PropTypes.bool,
  onBrowseRates: PropTypes.func.isRequired,
  rateViewerKey: PropTypes.string.isRequired,
  onScaleRead: PropTypes.func,
};
